import { getToken } from "Shared/baseURL";
import { fetchEventSource } from "@microsoft/fetch-event-source";

const resolveNotificationServiceBase = () => {
  const explicitBase =
    import.meta.env.VITE_NOTIFICATION_SERVICE_BASE ||
    import.meta.env.VITE_NMS_BASE_URL;

  if (explicitBase) {
    return String(explicitBase).replace(/\/$/, "");
  }

  const base = String(import.meta.env.VITE_BASEURL || "").replace(/\/$/, "");
  return base ? `${base}/nms` : "/api/nms";
};

const SSE_URL = `${resolveNotificationServiceBase()}/v1/sse/subscribe`;

let abortController = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_DELAY = 30000;
const BASE_RECONNECT_DELAY = 1000;

const isExpectedAbort = (error) =>
  error?.name === "AbortError" ||
  String(error?.message || "").includes("BodyStreamBuffer was aborted");

const getReconnectDelay = () => {
  const delay = Math.min(
    BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts),
    MAX_RECONNECT_DELAY,
  );
  reconnectAttempts++;
  return delay;
};

export const connectSSE = async (onMessage, onError, onOpen) => {
  disconnectSSE();

  const token = await getToken();
  if (!token) {
    onError?.(new Error("No auth token available"));
    return null;
  }

  const rawToken = token.startsWith("Bearer ") ? token.slice(7) : token;

  abortController = new AbortController();

  try {
    await fetchEventSource(SSE_URL, {
      signal: abortController.signal,
      openWhenHidden: true,
      headers: {
        Authorization: `Bearer ${rawToken}`,
      },
      onopen(response) {
        if (response.ok) {
          reconnectAttempts = 0;
          onOpen?.();
        } else {
          const error = new Error(`SSE open failed: ${response.status}`);
          error.status = response.status;
          throw error;
        }
      },
      onmessage(event) {
        if (!event.data) {
          return;
        }
        const trimmed = event.data.trim();
        if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
          try {
            const data = JSON.parse(trimmed);
            onMessage?.(data);
          } catch (parseError) {
            console.warn("[SSE] Failed to parse message:", parseError);
          }
        }
      },
      onerror(error) {
        if (isExpectedAbort(error)) {
          return;
        }

        onError?.(error);
        if (error?.status === 401 || error?.status === 403) {
          throw error;
        }
        return getReconnectDelay();
      },
    });
  } catch (error) {
    if (isExpectedAbort(error)) {
      return null;
    }
    onError?.(error);
    return null;
  }

  return true;
};

export const disconnectSSE = () => {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }

  reconnectAttempts = 0;
};

export const isSSEConnected = () => {
  return abortController !== null && !abortController.signal.aborted;
};
